import React from 'react';
import { Button } from 'antd';
import s from './s.module.less';

export interface FilterOption {
    label: string;
    value: string;
}

export interface FilterGroup {
    title: string;
    options: FilterOption[];
    selectedValues: string[];
    onSelectionChange: (values: string[]) => void;
}

interface FilterPanelProps {
    filterGroups: FilterGroup[];
    onClearAll: () => void;
    hasActiveFilters: boolean;
    isCollapsed?: boolean;
    onToggleCollapse?: () => void;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
    filterGroups,
    onClearAll,
    hasActiveFilters,
    isCollapsed = false,
    onToggleCollapse,
}) => {
    const handleOptionClick = (group: FilterGroup, optionValue: string) => {
        const currentValues = group.selectedValues;
        const newValues = currentValues.includes(optionValue)
            ? currentValues.filter((v) => v !== optionValue)
            : [...currentValues, optionValue];

        group.onSelectionChange(newValues);
    };

    return (
        <div className={s.filterPanel}>
            <div className={s.filterHeader}>
                <div className={s.filterTitle}>
                    <span className={s.titleText}>Filters</span>
                    {hasActiveFilters && (
                        <div className={s.filterCount}>
                            <span className={s.countNumber}>
                                {filterGroups.reduce((sum, group) => sum + group.selectedValues.length, 0)}
                            </span>
                        </div>
                    )}
                </div>
                <div
                    className={s.chevronIcon}
                    onClick={onToggleCollapse}
                    style={{ cursor: 'pointer' }}
                >
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        style={{
                            transform: isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
                            transition: 'transform 0.2s ease',
                        }}
                    >
                        <path d="M5 7.5L10 12.5L15 7.5" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
            </div>

            {hasActiveFilters && !isCollapsed && (
                <div className={s.clearAllSection}>
                    <Button
                        type="link"
                        onClick={onClearAll}
                        className={s.clearAllButton}
                    >
                        Clear all
                    </Button>
                </div>
            )}

            {!isCollapsed && (
                <div className={s.filterGroups}>
                    {filterGroups.map((group, groupIndex) => (
                        <div key={groupIndex} className={s.filterGroup}>
                            <div className={s.groupTitle}>{group.title}</div>
                            <div className={s.optionsContainer}>
                                {group.options.map((option, optionIndex) => {
                                    const isSelected = group.selectedValues.includes(option.value);
                                    return (
                                        <div
                                            key={optionIndex}
                                            className={`${s.filterOption} ${isSelected ? s.selected : ''}`}
                                            onClick={() => handleOptionClick(group, option.value)}
                                        >
                                            <span className={s.optionText}>{option.label}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default FilterPanel;
